import { Col } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { DATE_FORMAT } from 'common/constants/dateFormatConstants'
import dayjs from 'dayjs'
import Show from 'common/components/Show2/Show'
import { useTimezone } from 'common/hooks/useTimezone'
import { PatientScheduleSkeleton } from 'features/Home/Book/Booking/Schedule/PatientSchedule/PatientScheduleSkeleton/PatientScheduleSkeleton'
import { PatientSelection } from 'features/Home/Book/Booking/Schedule/PatientSchedule/PatientSelection/PatientSelection'
import { ScheduleCalendarWeek } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleWeekCalendar/ScheduleWeekCalendar'
import {
  ALLOW_RESCHEDULE_CURRENT_DAY_PAST_EVENTS,
  INFO_CONSTANTS,
} from 'features/Home/constants/infoConstants'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { ManageAppointment } from 'features/Schedule/pages/components/ManageAppointment/ManageAppointment'
import { ManageEhrAppointment } from 'features/Schedule/pages/components/ManageAppointment/ManageEhrAppointment'
import { useScheduleWeekActions } from 'features/Schedule/pages/components/ScheduleWeekView/hooks/useScheduleWeekActions'
import { useScheduleWeekData } from 'features/Schedule/pages/components/ScheduleWeekView/hooks/useScheduleWeekData'
import { type EScheduleView } from 'features/Schedule/pages/Schedule'
import { useSchedulePusher } from 'features/Schedule/pusher/useSchedulePusher'

import type { ScheduleMonth } from 'features/Schedule/api/schedule.types'

type Props = {
  defaultActiveDate: string | null
  selectedConsultationStaffId: string
  showCanceled: boolean
  scheduleView: EScheduleView
  scheduleMonthData?: ScheduleMonth
  zoomInterval: number
}

export const ScheduleWeekView = ({
  defaultActiveDate,
  selectedConsultationStaffId,
  showCanceled,
  scheduleView,
  scheduleMonthData,
  zoomInterval,
}: Props) => {
  const { timeZone } = useTimezone()

  useSchedulePusher(defaultActiveDate)

  const [editedPatient, setEditedPatient] = useState<IPatientSchedule | null>(null)
  const [addPatient, setAddPatient] = useState<boolean>(false)
  const [customAppointmentData, setCustomAppointmentData] = useState<{
    appointment: Date[]
    room: string
    date?: string
  } | null>(null)

  const {
    isLoadingWeek,
    isFetchingWeek,
    schedules,
    consultationStaffs,
    languages,
    timeInterval,
    disabledIntervals,
    weekDays,
    eventsToSendNotification,
    defaultAppointmentTime,
    patientStatuses,
    appointmentCategories,
    hasEhrBooking,
    byPassTemperatureCheck,
    rooms,
    appointmentColor,
    punctuality,
    patientBufferTime,
  } = useScheduleWeekData(defaultActiveDate, selectedConsultationStaffId, showCanceled)

  const { updateAppointment, notifyMultiplePatients, isNotifying, isNotifyingMultiple } =
    useScheduleWeekActions()

  useEffect(() => {
    if (!editedPatient?.id) return

    const latestPatient = schedules.find((schedule) => schedule.id === editedPatient.id)
    if (!latestPatient || latestPatient === editedPatient) return

    const hasEncounterChanged =
      latestPatient.emr_encounter_id !== editedPatient.emr_encounter_id ||
      latestPatient.emr_encounter_uuid !== editedPatient.emr_encounter_uuid ||
      latestPatient.emr_updated_at !== editedPatient.emr_updated_at

    if (hasEncounterChanged) {
      setEditedPatient(latestPatient)
    }
  }, [schedules, editedPatient])

  const requestedWeekStart = useMemo(() => {
    if (!defaultActiveDate) return null
    return dayjs(defaultActiveDate).startOf('week').format(DATE_FORMAT.PATIENT_TRACKER_DATE)
  }, [defaultActiveDate])

  const isWeekDataSynced = useMemo(() => {
    if (!requestedWeekStart) return false
    const weekDayKeys = Object.keys(weekDays)
    if (weekDayKeys.length === 0) return false
    return weekDayKeys.some(
      (dayKey) =>
        dayjs(dayKey).startOf('week').format(DATE_FORMAT.PATIENT_TRACKER_DATE) ===
        requestedWeekStart,
    )
  }, [weekDays, requestedWeekStart])

  const isLoading = !defaultActiveDate || isLoadingWeek || (isFetchingWeek && !isWeekDataSynced)

  const handleCloseModal = () => {
    setAddPatient(false)
    setEditedPatient(null)
    setCustomAppointmentData(null)
  }

  const createScheduleWithCustomAppointment = (start: Date, end: Date, date: string): void => {
    const defaultRoom = rooms[0]

    const appointmentData = {
      appointment: [start, end],
      room: `${defaultRoom.label}/${defaultRoom.value}`,
      date,
    }
    setCustomAppointmentData(appointmentData)
    setAddPatient(true)
  }

  const disabledInputsManageAppointment = useMemo(() => {
    if (!editedPatient) return false
    return !ALLOW_RESCHEDULE_CURRENT_DAY_PAST_EVENTS.includes(editedPatient?.status.code)
  }, [editedPatient])

  const handleUpdateEvents = useCallback(
    async (adjustedData: any, event: any, newDate?: string) => {
      return await updateAppointment(adjustedData, newDate || defaultActiveDate, event.id)
    },
    [defaultActiveDate],
  )

  const manageAppointmentTitle = useMemo(() => {
    if (editedPatient) {
      return INFO_CONSTANTS.EDIT_APPOINTMENT
    }
    return INFO_CONSTANTS.ADD_APPOINTMENT
  }, [editedPatient, defaultActiveDate])

  return (
    <Col xs={24}>
      <Show>
        <Show.When isVisible={isLoading}>
          <PatientScheduleSkeleton />
        </Show.When>
        <Show.Else>
          <ScheduleCalendarWeek
            key={requestedWeekStart ?? 'week'}
            sendInfoPatientsNotification={notifyMultiplePatients}
            setEditedPatient={setEditedPatient}
            createCustomAppointment={createScheduleWithCustomAppointment}
            handleUpdateEvents={handleUpdateEvents}
            showConsultationStaffInitials={selectedConsultationStaffId === '-1'}
            prepopulateTimeOnClick={defaultAppointmentTime}
            dayInterval={timeInterval}
            interval={zoomInterval}
            weekDays={weekDays}
            eventsToSendNotification={eventsToSendNotification}
            isSendingNotifications={isNotifying || isNotifyingMultiple}
            timeZone={timeZone}
            schedules={schedules}
            patientStatuses={patientStatuses}
            appointmentCategories={appointmentCategories}
            appointmentColor={appointmentColor}
            hasEhrBooking={hasEhrBooking}
          />
        </Show.Else>
      </Show>

      <Show>
        <Show.When isVisible={hasEhrBooking}>
          <ManageEhrAppointment
            patientBufferTime={patientBufferTime}
            scheduleView={scheduleView}
            patientStatuses={patientStatuses}
            title={manageAppointmentTitle}
            date={customAppointmentData?.date || defaultActiveDate}
            open={addPatient || !!editedPatient}
            isEditing={!!editedPatient}
            editedPatientData={editedPatient}
            setOpen={handleCloseModal}
            consultation_staffs={consultationStaffs}
            dayInterval={timeInterval}
            languages={languages}
            prePopulateSchedule={defaultAppointmentTime}
            disabledEverything={disabledInputsManageAppointment}
            customAppointment={customAppointmentData}
            rooms={rooms}
            schedules={schedules}
            timeInterval={timeInterval}
            disabledIntervals={disabledIntervals}
            timeZone={timeZone}
            appointmentCategories={appointmentCategories}
            isSendingNotifications={false}
            byPassTemperatureCheck={byPassTemperatureCheck}
            weekDays={weekDays}
            punctuality={punctuality}
            scheduleMonthData={scheduleMonthData}
          />
        </Show.When>
        <Show.Else>
          <ManageAppointment
            scheduleView={scheduleView}
            patientStatuses={patientStatuses}
            title={manageAppointmentTitle}
            date={customAppointmentData?.date || defaultActiveDate}
            open={addPatient || !!editedPatient}
            isEditing={!!editedPatient}
            editedPatientData={editedPatient}
            setOpen={handleCloseModal}
            consultation_staffs={consultationStaffs}
            dayInterval={timeInterval}
            languages={languages}
            prePopulateSchedule={defaultAppointmentTime}
            disabledEverything={disabledInputsManageAppointment}
            customAppointment={customAppointmentData}
            rooms={rooms}
            schedules={schedules}
            timeInterval={timeInterval}
            disabledIntervals={disabledIntervals}
            timeZone={timeZone}
            appointmentCategories={appointmentCategories}
            isSendingNotifications={false}
            byPassTemperatureCheck={byPassTemperatureCheck}
            weekDays={weekDays}
          />
        </Show.Else>
      </Show>

      <PatientSelection />
    </Col>
  )
}
